import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from './ui/Dialog';
import { apiClient } from '../lib/api-client';
import { getUtcMonthToDateRange } from '../lib/utils';
import { Cloud, Server, PoundSterling } from 'lucide-react';
import { Skeleton } from './ui/Skeleton';
import { Button } from './ui/Button';

interface CostBreakdown {
  [key: string]: number;
}

interface ProviderCost {
  provider: 'oci' | 'hetzner' | 'aws';
  totalCost: number;
  currency: string;
  originalCost: number;
  originalCurrency: string;
  breakdown: CostBreakdown;
}

interface CostData {
  costs: ProviderCost[];
  totalCost: number;
  currency?: string;
  period?: {
    start: string;
    end: string;
    days: number;
  };
}

interface CostReportDialogProps {
  open: boolean;
  onClose: () => void;
  tenantCode: string;
  tenantName: string;
}

const PROVIDER_NAMES: Record<string, string> = {
  oci: 'Oracle Cloud Infrastructure',
  hetzner: 'Hetzner Cloud',
  aws: 'Amazon Web Services',
};

const PROVIDER_ICONS: Record<string, React.ReactNode> = {
  oci: <Cloud className="w-5 h-5" />,
  hetzner: <Server className="w-5 h-5" />,
  aws: <Cloud className="w-5 h-5" />,
};

// Service label mappings for common services (fallback to service name if not found)
const SERVICE_LABELS: Record<string, Record<string, string>> = {
  oci: {
    'Compute': 'Compute',
    'Block Storage': 'Block Storage',
    'Object Storage': 'Object Storage',
    'Networking': 'Networking',
    'Load Balancing': 'Load Balancing',
    'FastConnect': 'FastConnect',
    'Database': 'Database',
    'Container Engine': 'Container Engine',
  },
  aws: {
    'Amazon Elastic Compute Cloud - Compute': 'EC2',
    'Amazon EC2-Other': 'EC2 Other',
    'Amazon Elastic Kubernetes Service': 'EKS',
    'Amazon Simple Storage Service': 'S3',
    'Amazon Route 53': 'Route 53',
    'AWS CloudTrail': 'CloudTrail',
    'Amazon CloudWatch': 'CloudWatch',
    'Amazon Relational Database Service': 'RDS',
    'Amazon Elastic Block Store': 'EBS',
    'Amazon Virtual Private Cloud': 'VPC',
  },
  hetzner: {
    servers: 'Servers',
    volumes: 'Volumes',
    loadBalancers: 'Load Balancers',
    floatingIps: 'Floating IPs',
    snapshots: 'Snapshots',
  },
};

export function CostReportDialog({ open, onClose, tenantCode, tenantName }: CostReportDialogProps) {
  const [costData, setCostData] = useState<CostData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && tenantCode) {
      fetchCostData();
    } else {
      setCostData(null);
      setError(null);
    }
  }, [open, tenantCode]);

  const fetchCostData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const now = new Date();
      const { start: firstOfMonth, end: today } = getUtcMonthToDateRange(now);

      const data = await apiClient.getCosts(tenantCode, undefined, firstOfMonth, today);
      setCostData(data);
    } catch (err: any) {
      console.error('Failed to fetch cost data:', err);
      setError(err.message || 'Failed to load cost data');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number | undefined, currency?: string) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return '£0.00';
    }
    const curr = currency || 'GBP';
    if (curr === 'GBP') {
      return `£${amount.toFixed(2)}`;
    } else if (curr === 'USD') {
      return `$${amount.toFixed(2)}`;
    } else if (curr === 'EUR') {
      return `€${amount.toFixed(2)}`;
    }
    return `${amount.toFixed(2)} ${curr}`;
  };

  const formatPercentage = (value: number | undefined, total: number | undefined) => {
    if (!value || !total || total === 0) return '0%';
    return `${((value / total) * 100).toFixed(1)}%`;
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogContent maxWidth="2xl">
        <DialogHeader>
          <DialogTitle>Cost Report - {tenantName}</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-6">
            {isLoading && (
              <div className="space-y-4 py-4">
                <Skeleton className="h-20 w-full" />
                <div className="space-y-2">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              </div>
            )}

            {error && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {!isLoading && !error && costData && costData.costs && (
              <>
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Cost (Month to Date)</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                        {formatCurrency(costData?.totalCost, costData?.currency || 'GBP')}
                      </p>
                    </div>
                    <PoundSterling className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                    By Cloud Provider
                  </h3>
                  
                  {costData?.costs?.map((providerCost) => {
                    if (!providerCost || providerCost.totalCost === 0) return null;
                    
                    return (
                      <div
                        key={providerCost.provider}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                      >
                        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                                {PROVIDER_ICONS[providerCost.provider]}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900 dark:text-white">
                                  {PROVIDER_NAMES[providerCost.provider]}
                                </p>
                                {providerCost.originalCurrency !== 'GBP' && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {formatCurrency(providerCost?.originalCost, providerCost?.originalCurrency)} converted
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-gray-900 dark:text-white">
                                {formatCurrency(providerCost?.totalCost, providerCost?.currency)}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {formatPercentage(providerCost?.totalCost || 0, costData?.totalCost || 0)} of total
                              </p>
                            </div>
                          </div>
                        </div>

                        {Object.keys(providerCost.breakdown).length > 0 && (
                          <div className="p-4 space-y-2">
                            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-3">
                              Service Breakdown
                            </p>
                            {Object.entries(providerCost.breakdown)
                              .filter(([_, value]) => value > 0)
                              .sort(([_, a], [__, b]) => b - a)
                              .map(([service, cost]) => {
                                const label = SERVICE_LABELS[providerCost.provider]?.[service] || service;
                                return (
                                  <div
                                    key={service}
                                    className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800 last:border-0"
                                  >
                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                      {label}
                                    </span>
                                    <div className="text-right">
                                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                        {formatCurrency(cost, providerCost?.currency)}
                                      </span>
                                      <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                        {formatPercentage(cost, providerCost?.totalCost || 0)}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {costData?.costs && costData.costs.filter(c => c?.totalCost > 0).length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No cost data available for this period.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

